/**
 * The noBGP iOS and Android apps (mobile/ in this repo) load this console in a native WebView and
 * append `noBGP-App` to its user agent (capacitor.config.json `appendUserAgent`). These rules read
 * that, so the console can change what it draws inside the app and ONLY inside the app: a browser,
 * including a phone browser, never matches.
 *
 * Pure, so `npm test` checks them; `lib/nativeApp.ts` applies them to `navigator.userAgent`.
 */

export const NATIVE_APP_MARK = 'noBGP-App'

/** The console is running inside the noBGP app. */
export const isNativeAppAgent = (userAgent: string): boolean => userAgent.includes(NATIVE_APP_MARK)

/**
 * A phone, not a tablet. ⚠ By the DEVICE, never by the width: an iPhone in landscape is wider than
 * the `sm` breakpoint and would flip to the tablet layout on every rotation. iPadOS reports a
 * desktop (Macintosh) agent in a WebView, which correctly reads as not a phone; an Android tablet
 * omits `Mobile`.
 */
export const isPhoneAgent = (userAgent: string): boolean =>
  /\b(iPhone|iPod)\b/.test(userAgent) || (/\bAndroid\b/.test(userAgent) && /\bMobile\b/.test(userAgent))

/**
 * How the console lays itself out. `phone`: the app on a phone, which shows the drawer (Nodes,
 * Networks, Account) full screen and no graph. `tablet` and `web` keep the graph with the drawer
 * over it.
 */
export type ConsoleLayout = 'web' | 'tablet' | 'phone'

export const consoleLayout = (userAgent: string): ConsoleLayout =>
  !isNativeAppAgent(userAgent) ? 'web' : isPhoneAgent(userAgent) ? 'phone' : 'tablet'

/**
 * A page of one node, network or account (`/nodes/<id>`, `/networks/<id>`, `/account/<id>`), as
 * opposed to a tab's list or the empty canvas. On a phone, a move to one shows the List view
 * (PhoneShell.tsx), because those pages live in the drawer.
 */
export const isDetailPath = (pathname: string): boolean => /^\/(nodes|networks|account)\/[^/]+/.test(pathname)

/** The views of the noBGP app on a phone, chosen in the bar at the bottom (PhoneShell.tsx). */
export type PhoneView = 'list' | 'graph' | 'map'
